import type { Extension } from '@tiptap/react';
import { EditorContent } from '@tiptap/react';
import { useTranslations } from 'next-intl';
import { type FC, useEffect, useRef } from 'react';
import { BaseLineInfo } from '@/app/components/icons/BaseLineInfo';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { useCustomMention } from '@/app/components/shared/MentionsTextarea/hooks/use-custom-mention';
import { toast } from '@/app/components/ui/use-toast';
import { useEditorWithExtensions } from '@/app/hooks/editor';
import type { ElementModality, MentionItem, ReferencePrompt, TextPromptWithInputItem } from '@/app/types';
import { cn, extractMentionsIdsFromEditor } from '@/app/utils';

interface RichTextFieldProps {
  field: string;
  value: TextPromptWithInputItem;
  setValue: (value: TextPromptWithInputItem) => void;
  maxLength: number;
  className?: string;
  allowMention?: boolean;
  info?: string;
  placeholder?: string;
  onBeforeAddMention?: (mentionItem: MentionItem) => boolean;
  onBeforeDeleteMention?: (mentionsIdsToDelete: string[], updatedContent: TextPromptWithInputItem) => boolean;
  getExtraItems?: () => ReferencePrompt[];
  // false 表示不阻塞默认粘贴逻辑，true 表示阻塞默认粘贴逻辑
  onPaste?: (clipboardContent: string) => boolean;
  // 撤销回调，返回 true 表示已处理撤销操作，阻止默认行为
  onUndo?: () => boolean;
  /** 允许的 modality 筛选 */
  allowedModalities?: Array<ElementModality>;
}

export const RichTextField: FC<RichTextFieldProps> = ({
  field,
  value,
  setValue,
  maxLength,
  className,
  allowMention = false,
  info,
  placeholder,
  onBeforeAddMention,
  onBeforeDeleteMention,
  getExtraItems,
  onPaste,
  onUndo,
  allowedModalities,
}) => {
  const t = useTranslations();
  const previousMentionsIds = useRef<string[]>([]);

  const { mentionExtension, onUpdateCallback } = useCustomMention({
    previousMentionsIds,
    onBeforeAddMention,
    onBeforeDeleteMention,
    customMentionOptions: {
      // @ts-expect-error showTooltip is provided by CustomMention.extend but TypeScript doesn't recognize it
      showTooltip: true,
    },
    getExtraItems,
    allowedModalities,
  });

  const { editor, isEmpty } = useEditorWithExtensions({
    content: value,
    updateContent: setValue,
    maxLength: maxLength,
    onMaxLengthExceeded: () => {
      toast({
        type: 'warning',
        message: t('create.createForm.error.invalidLength', { limit: maxLength }),
      });
    },
    paragraphClassName: 'inline',
    extensions: allowMention ? [mentionExtension as Extension] : [],
    editorProps: {
      attributes: {
        class: 'inline',
      },
      handleKeyDown: (_view, event) => {
        if (event.key === 'Tab' && isEmpty && placeholder) {
          event.preventDefault();
          // 按 tab 键时，将 placeholder 设置为当前值
          setValue([{ type: 'text', text: placeholder }]);
          return true; // 表示事件已处理
        }

        // 处理撤销操作
        if ((event.metaKey || event.ctrlKey) && event.key === 'z' && !event.shiftKey) {
          const handled = onUndo?.();
          if (handled) {
            event.preventDefault();
            event.stopPropagation();
            return true;
          }
        }

        return false; // 让其他 keydown 处理器处理事件
      },
      handlePaste: (_view, event, _slice) => {
        // 获取剪贴板文本内容
        const clipboardContent = event.clipboardData?.getData('text/plain') || '';

        // 调用用户提供的 onPaste 回调
        return onPaste?.(clipboardContent);
      },
    },
    onUpdateCallback,
  });

  // 不管内部更新（editor变化）还是外部更新（value变化）都更新 previousMentionsIds
  useEffect(() => {
    if (editor) {
      previousMentionsIds.current = extractMentionsIdsFromEditor(editor);
    }
  }, [editor, value]);

  return (
    <>
      <div className="mt-1.5 flex items-center gap-1 text-system-white64 text-xs">
        {field} <span className="self-start text-system-error">*</span>
        <CustomTooltip content={info} disableHoverableContent={true}>
          <BaseLineInfo className="shrink-0 text-system-white64 text-xs" />
        </CustomTooltip>
      </div>
      <div className={cn('relative', className)}>
        <span className="text-system-white36">{'['}</span>
        <EditorContent
          editor={editor}
          className={cn('inline caret-system-blue02', isEmpty && 'inline-block w-[1px]')}
        />
        {isEmpty && (
          <span
            className="cursor-text select-none text-system-white24 leading-7"
            onClick={() => editor?.commands.focus()}
          >
            <span>{placeholder}</span>
            <span className="ml-1 inline-block rounded-6 border border-system-white12 bg-system-white10 px-2 py-0.5 text-xs">
              tab
            </span>
          </span>
        )}
        <span className="text-system-white36">{'],'}</span>
      </div>
    </>
  );
};
